import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Button, Select, MenuItem, FormControl,
  InputLabel, TextField, Rating, IconButton, Snackbar, Alert, Modal
} from "@mui/material";
import { AddCircleOutline, Cancel } from "@mui/icons-material";
import axios from "axios";
import { localAxios, localAxiosGet } from "../../../Axios/axios";

const predefinedAppraisalCategories = [
  "Communication Skills", "Teamwork & Collaboration", "Problem-Solving",
  "Technical Proficiency", "Leadership", "Time Management",
  "Adaptability", "Customer Focus", "Innovation", "Quality of Work",
];

const EditPerformanceReviewAndRatings = ({ employeeId, onClose, open }) => {

  const appraisalYear = new Date().getFullYear();
  const [currentApparaisalIdForEdit, setCurrentApparaisalIdForEdit] = useState(null);
  const userId = localStorage.getItem('userId')
  const [appraisalPeriod, setAppraisalPeriod] = useState("");
  const [categories, setCategories] = useState([]);
  const [feedback, setFeedback] = useState({
    strengths: "",
    areasForImprovement: "",
    goalsForNextPeriod: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (open && employeeId) {
      fetchCurrentApparaisalId();
    }
  }, [open, employeeId]);

  useEffect(() => {
    if (currentApparaisalIdForEdit) {
      fetchAppraisal();
    }
  }, [currentApparaisalIdForEdit]);


  const fetchCurrentApparaisalId = async () => {
    try {
      const response = await axios(
        localAxiosGet(`/performancemanagement/getRatingsAndFeedback?emp_id=${employeeId}&appraisal_year=${appraisalYear}&appraisal_period=${appraisalPeriod}`)
      );
      if (response.data.status === 'SUCCESS') {
        setCurrentApparaisalIdForEdit(response.data.result.id);
      }
    } catch (err) {
      console.error("Error fetching past appraisals:", err);

    }
  }

  const fetchAppraisal = async () => {
    try {
      const response = await axios(localAxiosGet(`/performancemanagement/getAppraisalById?appraisal_id=${currentApparaisalIdForEdit}`));
      if (response.data.statusCode === 200) {
        const data = response.data.result;
        setAppraisalPeriod(data.appraisal_period || "");
        setFeedback({
          strengths: data.strengths || "",
          areasForImprovement: data.area_of_improvement || "",
          goalsForNextPeriod: data.goals_for_next_period || "",
        });

        setCategories(
          data.ratings.map((r) => ({
            id: r.id,
            name: r.category_name,
            rating: r.rating,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching appraisal:", error);
    }
  };

  const handleCategoryChange = (id, field, value) => {
    setCategories((prev) =>
      prev.map((cat) => (cat.id === id ? { ...cat, [field]: value } : cat))
    );
  };

  const handleRemoveCategory = (id) => {
    setCategories((prev) => prev.filter((cat) => cat.id !== id));
  };

  const handleAddCategory = () => {
    const newId = categories.length ? categories[categories.length - 1].id + 1 : 1;
    setCategories([...categories, { id: newId, name: "", rating: 0 }]);
  };

  // 🔹 Submit updated appraisal
  const handleUpdateAppraisal = async () => {

    try {
      const payload = {
        appraisal_id: currentApparaisalIdForEdit,
        report_manager_id: userId,
        emp_id: employeeId,
        appraisal_id: currentApparaisalIdForEdit,
        appraisal_year: appraisalYear,
        appraisal_period: appraisalPeriod,
        strengths: feedback.strengths,
        area_of_improvement: feedback.areasForImprovement,
        goals_for_next_period: feedback.goalsForNextPeriod,
        ratings: categories.map((c) => ({
          categoryName: c.name,
          rating: c.rating,
        })),
      };

      const response = await axios(localAxios("/performancemanagement/addRatings", payload));
      if (response.data.statusCode === 200) {
        setSnackbar({ open: true, message: "Appraisal updated successfully!", severity: "success" });
        await fetchAppraisal();
        setTimeout(onClose, 2000);
      }
    } catch (error) {
      console.error("Error updating appraisal:", error);
      setSnackbar({ open: true, message: "Failed to update appraisal.", severity: "error" });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
    >
      <Box
        sx={{
          maxWidth: "md",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          margin: "auto",
          bgcolor: "white",
          borderRadius: 3,
          p: 2,
          boxShadow: 24,
          overflowY: "auto"
        }}
      >
        <Box sx={{ p: 3, bgcolor: "white", borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>Edit Appraisal</Typography>

          {/* Appraisal Period */}
          <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
            <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
              <InputLabel id="appraisal-period-label">Appraisal Period</InputLabel>
              <Select
                labelId="appraisal-period-label"
                id="edit-appraisal-period-select"
                value={appraisalPeriod}
                label="Appraisal Period"
                onChange={(e) => setAppraisalPeriod(e.target.value)}
              >
                <MenuItem value="Q1">Quarter 1</MenuItem>
                <MenuItem value="Q2">Quarter 2</MenuItem>
                <MenuItem value="Q3">Quarter 3</MenuItem>
                <MenuItem value="Q4">Quarter 4</MenuItem>

                {/* Half-Yearly */}
                <MenuItem value="H1">Half-Yearly (Jan - Jun)</MenuItem>
                <MenuItem value="H2">Half-Yearly (Jul - Dec)</MenuItem>

                {/* Yearly */}
                <MenuItem value="Yearly">Yearly</MenuItem>
              </Select>
            </FormControl>

            {/* Categories */}
            {categories.map((cat) => (
              <Box key={cat.id} sx={{ mb: 2, border: "1px solid #eee", borderRadius: 2, p: 2, position: "relative" }}>
                <IconButton
                  onClick={() => handleRemoveCategory(cat.id)}
                  size="small"
                  color="error"
                  sx={{ position: "absolute", top: 4, right: 4 }}
                >
                  <Cancel />
                </IconButton>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        labelId="category-label"
                        id="edit-category-select"
                        label="Category"
                        value={cat.name}
                        onChange={(e) => handleCategoryChange(cat.id, "name", e.target.value)}
                      >
                        {predefinedAppraisalCategories.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">Rating:</Typography>
                    <Rating
                      value={cat.rating}
                      precision={0.5}
                      onChange={(e, newValue) => handleCategoryChange(cat.id, "rating", newValue)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={handleAddCategory}
              sx={{ mb: 3 }}
            >
              Add Category
            </Button>

            {/* Feedback */}
            <TextField
              label="Strengths"
              fullWidth
              multiline
              rows={3}
              value={feedback.strengths}
              onChange={(e) => setFeedback({ ...feedback, strengths: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Areas for Improvement"
              fullWidth
              multiline
              rows={3}
              value={feedback.areasForImprovement}
              onChange={(e) => setFeedback({ ...feedback, areasForImprovement: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Goals for Next Period"
              fullWidth
              multiline
              rows={3}
              value={feedback.goalsForNextPeriod}
              onChange={(e) => setFeedback({ ...feedback, goalsForNextPeriod: e.target.value })}
              sx={{ mb: 2 }}
            />
          </Box>


          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              borderTop: "1px solid #eee",
              pt: 2,
              mt: 2,
              bgcolor: "white",

            }}
          >
            <Button variant="outlined" color="primary" onClick={onClose}>
              Close
            </Button>
            <Button variant="contained" color="primary" onClick={handleUpdateAppraisal}>
              Save Changes
            </Button>
          </Box>

          <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
          </Snackbar>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditPerformanceReviewAndRatings;
